/**
 * useTasks.ts — Evo Brain Phase 1
 * React Query CRUD over /api/tasks. Mirrors the local todo-system API surface
 * (create / patch / approve / retry / run_now). No claim/complete — those are
 * internal to the worker runner (single writer).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export type TaskStatus =
  | 'pending' | 'needs_approval' | 'approved'
  | 'in_progress' | 'done' | 'failed' | 'blocked';

export interface Task {
  id: string;
  title: string;
  description: string;
  type: string;
  priority: string;          // 'alta' | 'media' | 'baja'
  category: string;
  outputMode: string;        // 'per_run' | 'single_doc'
  status: TaskStatus;
  attempts: number;
  result: string | null;
  lastError: string | null;
  nextRunAt: string | null;
  repeatMinutes: number | null;
  repeatUntil: string | null;
  maxRuns: number | null;
  runs: number;
  contactId: number | null;
  projectId: number | null;
  runStartedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // ── Evo Brain (migración 0073) — puente propuesta→task ─────────────────────
  proposedActionJson: string | null;
  entityType: string | null;
  entityId: string | null;
  source: string;              // 'user' | 'evo-brain'
  conversationId: number | null;
  proposalMessageId: number | null;
  executor: string;            // 'agent-cli' | 'tool'
  // Línea en castellano llano de qué va a pasar al ejecutar — null si el
  // creador no la proveyó (nunca se inventa en el frontend).
  plainSummary: string | null;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  type?: string;
  priority?: string;
  category?: string;
  outputMode?: string;
  nextRunAt?: string | null;
  repeatMinutes?: number | null;
  repeatUntil?: string | null;
  maxRuns?: number | null;
  contactId?: number | null;
  projectId?: number | null;
}

export type RunMode = 'approval' | 'autonomous';

export interface RunnerStatus {
  mode: RunMode;
  paused: boolean;
  dailyCap: number;
  runsToday: number;
  capReached: boolean;
  day: string;
}

export function useRunnerStatus() {
  return useQuery<RunnerStatus>({
    queryKey: ['tasks-runner-status'],
    queryFn: () => api.get<RunnerStatus>('/api/tasks/runner-status'),
    refetchInterval: 15_000,
  });
}

function invalidateRunner(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['tasks-runner-status'] });
}

export function usePauseRunner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RunnerStatus>('/api/tasks/runner/pause', {}),
    onSuccess: () => invalidateRunner(qc),
  });
}

export function useResumeRunner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RunnerStatus>('/api/tasks/runner/resume', {}),
    onSuccess: () => invalidateRunner(qc),
  });
}

export function useSetRunMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: RunMode) => api.post<RunnerStatus>('/api/tasks/runner/mode', { mode }),
    onSuccess: () => invalidateRunner(qc),
  });
}

export function useBulkDeleteTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<{ deleted: number; skippedInProgress: number }>('/api/tasks/bulk/delete', { ids }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tasks'] }); },
  });
}

export function useBulkRetryTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<{ retried: number; skippedInProgress: number }>('/api/tasks/bulk/retry', { ids }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tasks'] }); },
  });
}

export function useTasks(filter: { status?: string; contactId?: number; projectId?: number } = {}) {
  return useQuery<{ tasks: Task[] }>({
    queryKey: ['tasks', filter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter.status) qs.set('status', filter.status);
      if (filter.contactId != null) qs.set('contactId', String(filter.contactId));
      if (filter.projectId != null) qs.set('projectId', String(filter.projectId));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api.get<{ tasks: Task[] }>(`/api/tasks${suffix}`);
    },
    refetchInterval: 15_000, // in_progress tasks advance in the worker — keep the list fresh
  });
}

export function useTask(id: string | null) {
  return useQuery<Task>({
    queryKey: ['task', id],
    queryFn: () => api.get<Task>(`/api/tasks/${id}`),
    enabled: id !== null,
    refetchInterval: 15_000,
  });
}

export interface TaskWorkspaceFile {
  name: string;
  sizeBytes: number;
  modifiedAt: string;
}

export function useTaskWorkspaceFiles(id: string | null) {
  return useQuery<{ files: TaskWorkspaceFile[] }>({
    queryKey: ['task-workspace-files', id],
    queryFn: () => api.get<{ files: TaskWorkspaceFile[] }>(`/api/tasks/${id}/workspace-files`),
    enabled: id !== null,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: ['tasks'] });
  if (id) void qc.invalidateQueries({ queryKey: ['task', id] });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.post<Task>('/api/tasks', input),
    onSuccess: () => invalidate(qc),
  });
}

export function usePatchTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<CreateTaskInput>) =>
      api.patch<Task>(`/api/tasks/${id}`, patch),
    onSuccess: (_d, vars) => invalidate(qc, vars.id),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/api/tasks/${id}`),
    onSuccess: () => invalidate(qc),
  });
}

export function useApproveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Task>(`/api/tasks/${id}/approve`, {}),
    onSuccess: (_d, id) => invalidate(qc, id),
  });
}

// executor='tool' (propuesta de Evo Brain): approve ejecuta in-server y la
// respuesta trae {ok, summary, task} en vez de un Task bare — shape distinto
// al de una task agent-cli normal (ver routes/tasks.ts POST /:id/approve).
export interface ApproveProposalResult { ok: boolean; summary?: string; task: Task }

export function useApproveProposalTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, remember }: { id: string; remember?: boolean }) =>
      api.post<ApproveProposalResult>(`/api/tasks/${id}/approve`, { rememberApproval: Boolean(remember) }),
    onSuccess: (_d, { id }) => invalidate(qc, id),
  });
}

export function useRejectProposalTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean }>(`/api/tasks/${id}/reject`, {}),
    onSuccess: (_d, id) => invalidate(qc, id),
  });
}

export function useRetryTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Task>(`/api/tasks/${id}/retry`, {}),
    onSuccess: (_d, id) => invalidate(qc, id),
  });
}

export function useRunTaskNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Task>(`/api/tasks/${id}/run-now`, {}),
    onSuccess: (_d, id) => invalidate(qc, id),
  });
}
